/**
 * AI Settings Panel
 * Admin configuration for OpenAI API key
 */

import { useState } from 'react';
import { Eye, EyeOff, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useAISettings, useUpdateAISettings, useTestAIConnection } from '@/hooks/useAssistants';
import { toast } from 'sonner';
import { asApiError } from '@/utils/error.util';

export const AISettingsPanel = () => {
  const { data: settings, isLoading } = useAISettings();
  const updateMutation = useUpdateAISettings();
  const testMutation = useTestAIConnection();

  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);

  const handleSave = async () => {
    if (!apiKey.trim()) {
      toast.error('Моля, въведете API ключ');
      return;
    }

    try {
      await updateMutation.mutateAsync(apiKey);
      toast.success('API ключът е запазен');
      setApiKey('');
    } catch (error) {
      toast.error(asApiError(error).message || 'Грешка при запазване');
    }
  };

  const handleTest = async () => {
    try {
      const result = await testMutation.mutateAsync();
      if (result.success) {
        toast.success(`Връзката е успешна (модел: ${result.model})`);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error(asApiError(error).message || 'Грешка при тестване');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-500 dark:text-dark-text-muted">
        Конфигурирайте OpenAI API ключ за AI ботовете. Ключът се използва за генериране на отговори от @assistant и @research.
      </p>

      {/* Status */}
      <div className="flex items-center gap-2">
        {settings?.hasApiKey ? (
          <>
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-sm text-green-600 dark:text-green-400">API ключ е конфигуриран</span>
          </>
        ) : (
          <>
            <XCircle className="w-4 h-4 text-red-500" />
            <span className="text-sm text-red-600 dark:text-red-400">API ключ не е конфигуриран</span>
          </>
        )}
      </div>

      {/* API Key Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
          OpenAI API ключ
        </label>
        <div className="relative">
          <input
            type={showKey ? 'text' : 'password'}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={settings?.hasApiKey ? '••••••••••••••••' : 'sk-...'}
            className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-dark-border-primary rounded-lg bg-white dark:bg-dark-bg-input text-gray-900 dark:text-dark-text-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            type="button"
            onClick={() => setShowKey(!showKey)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <p className="mt-1 text-xs text-gray-400 dark:text-dark-text-muted">
          Вземете ключ от{' '}
          <span className="text-blue-500">platform.openai.com/api-keys</span>
        </p>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={handleSave}
          disabled={!apiKey.trim() || updateMutation.isPending}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
        >
          {updateMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
          Запази
        </button>

        <button
          onClick={handleTest}
          disabled={!settings?.hasApiKey || testMutation.isPending}
          className="px-4 py-2 border border-gray-300 dark:border-dark-border-primary text-gray-700 dark:text-dark-text-secondary rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-hover disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
        >
          {testMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
          Тест връзка
        </button>
      </div>

      {/* Supported Models */}
      <div className="mt-8 p-4 bg-gray-50 dark:bg-dark-bg-hover rounded-lg">
        <h3 className="text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
          Поддържани модели
        </h3>
        <div className="space-y-1 text-sm text-gray-500 dark:text-dark-text-muted">
          <p><span className="font-mono text-xs bg-gray-200 dark:bg-dark-bg-input px-1.5 py-0.5 rounded">gpt-5</span> — Основен модел, високо качество</p>
          <p><span className="font-mono text-xs bg-gray-200 dark:bg-dark-bg-input px-1.5 py-0.5 rounded">gpt-5-mini</span> — Бърз и икономичен</p>
        </div>
      </div>
    </div>
  );
};
